import React, { useEffect, useState } from "react";
import TopBar from "@/components/TopBar";
import { api, formatApiError } from "@/lib/api";
import { Target, ShieldCheck, Crosshair, Waves, Mail, Monitor, Key, Copy, Trash2, LogOut, CheckCircle, XCircle, Plus, AlertTriangle, Shield, Save, MessageCircle, Webhook, Bell, ToggleLeft, ToggleRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import TwoFactorPanel from "@/components/TwoFactorPanel";
import KycPanel from "@/components/KycPanel";
import { toast } from "sonner";

export default function Profile() {
  const { user, setUser } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [newKeyResult, setNewKeyResult] = useState(null);
  const [verifyBusy, setVerifyBusy] = useState(false);
  const [riskLimits, setRiskLimits] = useState({ max_daily_loss: "", max_concurrent_trades: "", max_position_size: "" });
  const [riskSaving, setRiskSaving] = useState(false);
  const [usernameDraft, setUsernameDraft] = useState(user?.username || "");
  const [usernameSaving, setUsernameSaving] = useState(false);
  const [botSettings, setBotSettings] = useState(null);
  const [botForm, setBotForm] = useState({ telegram_chat_id: "", discord_webhook_url: "" });
  const [botBusy, setBotBusy] = useState(false);
  const [botEvents, setBotEvents] = useState([]);

  useEffect(() => {
    let active = true;
    api.get("/gamification/stats").then(({ data }) => {
      if (active) setStats(data ?? {});
    }).catch(() => {});
    api.get("/account/sessions").then(({ data }) => {
      if (active) setSessions(Array.isArray(data) ? data : []);
    }).catch(() => {});
    api.get("/account/api-keys").then(({ data }) => {
      if (active) setApiKeys(Array.isArray(data) ? data : []);
    }).catch(() => {});
    api.get("/account/risk-limits").then(({ data }) => {
      if (active) setRiskLimits({
        max_daily_loss: data?.max_daily_loss ?? "",
        max_concurrent_trades: data?.max_concurrent_trades ?? "",
        max_position_size: data?.max_position_size ?? "",
      });
    }).catch(() => {});
    api.get("/bots/settings").then(({ data }) => {
      if (active) {
        setBotSettings(data ?? {});
        setBotForm({ telegram_chat_id: data?.telegram_chat_id || "", discord_webhook_url: data?.discord_webhook_url || "" });
        setBotEvents(data.events || []);
      }
    }).catch(() => {});
    return () => { active = false; };
  }, []);

  const badgeIcons = {
    Target,
    ShieldCheck,
    Crosshair,
    Waves
  };

  const resendVerification = async () => {
    setVerifyBusy(true);
    try {
      const { data } = await api.get("/auth/resend-verification");
      if (data.verification_token) {
        navigator.clipboard.writeText(data.verification_token).catch(() => {});
        toast.success(t("toast_verification_token_copied"));
      } else {
        toast.success(t("toast_verification_link_sent"));
      }
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    } finally { setVerifyBusy(false); }
  };

  const verifyEmail = async () => {
    const token = prompt(t("verify_prompt"));
    if (!token) return;
    try {
      await api.post("/auth/verify-email", { token });
      toast.success(t("toast_email_verified"));
      if (setUser) setUser({ ...user, email_verified: true });
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    }
  };

  const revokeSession = async (id) => {
    try {
      await api.delete(`/account/sessions/${id}`);
      setSessions((prev) => prev.filter((s) => s.id !== id));
      toast.success(t("toast_session_revoked"));
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    }
  };

  const logoutAll = async () => {
    if (!window.confirm(t("confirm_logout_all"))) return;
    try {
      await api.post("/account/sessions/logout-all");
      setSessions([]);
      toast.success(t("toast_all_sessions_closed"));
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) return;
    try {
      const { data } = await api.post("/account/api-keys", { name: newKeyName.trim() });
      setNewKeyResult(data.key);
      setNewKeyName("");
      api.get("/account/api-keys").then(({ data: keys }) => setApiKeys(keys)).catch(() => {});
      toast.success(t("toast_api_key_created"));
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    }
  };

  const deleteApiKey = async (id) => {
    try {
      await api.delete(`/account/api-keys/${id}`);
      setApiKeys((prev) => prev.filter((k) => k.id !== id));
      toast.success(t("toast_api_key_deleted"));
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => toast.success(t("toast_copied"))).catch(() => {});
  };

  const connectTelegram = async () => {
    if (!botForm.telegram_chat_id.trim()) return;
    setBotBusy(true);
    try {
      const { data } = await api.post("/bots/telegram", { chat_id: botForm.telegram_chat_id.trim() });
      setBotSettings(prev => ({ ...prev, telegram_chat_id: data.telegram_chat_id, enabled: true, telegram_enabled: true }));
      toast.success(t("bot_telegram_connected"));
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || er.message); }
    finally { setBotBusy(false); }
  };

  const disconnectTelegram = async () => {
    setBotBusy(true);
    try {
      await api.delete("/bots/telegram");
      setBotSettings(prev => ({ ...prev, telegram_chat_id: "" }));
      setBotForm(f => ({ ...f, telegram_chat_id: "" }));
      toast.success(t("bot_telegram_removed"));
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || er.message); }
    finally { setBotBusy(false); }
  };

  const connectDiscord = async () => {
    if (!botForm.discord_webhook_url.trim()) return;
    setBotBusy(true);
    try {
      const { data } = await api.post("/bots/discord", { webhook_url: botForm.discord_webhook_url.trim() });
      setBotSettings(prev => ({ ...prev, discord_webhook_url: data.discord_webhook_url, enabled: true, discord_enabled: true }));
      toast.success(t("bot_discord_connected"));
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || er.message); }
    finally { setBotBusy(false); }
  };

  const disconnectDiscord = async () => {
    setBotBusy(true);
    try {
      await api.delete("/bots/discord");
      setBotSettings(prev => ({ ...prev, discord_webhook_url: "" }));
      setBotForm(f => ({ ...f, discord_webhook_url: "" }));
      toast.success(t("bot_discord_removed"));
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || er.message); }
    finally { setBotBusy(false); }
  };

  const toggleBotEvent = (evt) => {
    setBotEvents(prev => prev.includes(evt) ? prev.filter(e => e !== evt) : [...prev, evt]);
  };

  const saveBotSettings = async () => {
    setBotBusy(true);
    try {
      await api.post("/bots/preferences", {
        enabled: botSettings?.enabled !== false,
        telegram_enabled: botSettings?.telegram_enabled !== false,
        discord_enabled: botSettings?.discord_enabled !== false,
        events: botEvents,
      });
      toast.success(t("bot_saved"));
    } catch (er) { toast.error(formatApiError(er.response?.data?.detail) || er.message); }
    finally { setBotBusy(false); }
  };

  const saveRiskLimits = async () => {
    setRiskSaving(true);
    try {
      const body = {};
      if (riskLimits.max_daily_loss !== "") body.max_daily_loss = Number(riskLimits.max_daily_loss);
      if (riskLimits.max_concurrent_trades !== "") body.max_concurrent_trades = Number(riskLimits.max_concurrent_trades);
      if (riskLimits.max_position_size !== "") body.max_position_size = Number(riskLimits.max_position_size);
      await api.post("/account/risk-limits", body);
      toast.success(t("toast_risk_limits_saved"));
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    } finally { setRiskSaving(false); }
  };

  const saveUsername = async () => {
    const val = usernameDraft.trim();
    if (!val) return;
    setUsernameSaving(true);
    try {
      const { data } = await api.post("/account/username", { username: val });
      if (setUser) setUser({ ...user, username: data.username });
      toast.success(t("toast_saved"));
    } catch (er) {
      toast.error(formatApiError(er.response?.data?.detail) || er.message);
    } finally { setUsernameSaving(false); }
  };

  return (
    <div className="min-h-screen bg-app text-white">
      <TopBar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6">{t("profile")}</h1>

        {/* Account info */}
        <div className="bg-panel border border-line rounded-sm p-5 sm:p-6 space-y-3 mb-6">
          <Row k={t("name")} v={user?.name} />
          <div className="flex justify-between items-center border-b border-subtle pb-2">
            <span className="text-[10px] uppercase tracking-widest text-dim">{t("username")}</span>
            <div className="flex items-center gap-2">
              <input value={usernameDraft} onChange={e => setUsernameDraft(e.target.value)}
                className="w-36 bg-app border border-line rounded-sm px-2 py-1 text-xs font-mono outline-none focus:border-buy text-right"
                placeholder={user?.username || user?.id?.slice(-8)} maxLength={24} />
              <button onClick={saveUsername} disabled={usernameSaving}
                className="text-[10px] bg-buy text-black px-2 py-1 rounded-sm font-bold hover:bg-emerald-400 disabled:opacity-50 uppercase tracking-widest">
                {usernameSaving ? "..." : t("save")}
              </button>
            </div>
          </div>
          <Row k={t("email")} v={user?.email} />
          <div className="flex justify-between items-center border-b border-subtle pb-2">
            <span className="text-[10px] uppercase tracking-widest text-dim">{t("email_verification")}</span>
            <div className="flex items-center gap-2">
              {user?.email_verified ? (
                <span className="inline-flex items-center gap-1 text-xs text-buy font-mono"><CheckCircle className="w-3.5 h-3.5" /> {t("verified")}</span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-sell font-mono"><XCircle className="w-3.5 h-3.5" /> {t("unverified")}</span>
              )}
            </div>
          </div>
          <Row k={t("role")} v={user?.role} />
          <Row k={t("account_mode")} v={user?.account_mode} />
          <Row k={t("demo_account")} v={`$${Number(user?.demo_balance ?? 0).toFixed(2)}`} />
          <Row k={t("real_account")} v={`$${Number(user?.real_balance ?? 0).toFixed(2)}`} />
          <Row k={t("member_since")} v={user?.created_at ? new Date(user.created_at).toLocaleDateString() : "—"} />
          {!user?.email_verified && (
            <div className="mt-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-sm flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 shrink-0" />
              <div className="flex-1">
                <p className="text-xs text-yellow-200">{t("verify_required")}</p>
              </div>
              <div className="flex gap-2">
                <button onClick={verifyEmail} className="text-xs bg-buy text-black px-3 py-1.5 rounded-sm font-bold hover:bg-emerald-400">{t("verify")}</button>
                <button onClick={resendVerification} disabled={verifyBusy} className="text-xs border border-line px-3 py-1.5 rounded-sm hover:bg-panel-hover disabled:opacity-50">{verifyBusy ? "..." : t("resend")}</button>
              </div>
            </div>
          )}
          <div className="pt-4 border-t border-line">
            <div className="text-[10px] uppercase tracking-widest text-dim mb-2">{t("switch_language")}</div>
            <div className="flex gap-2 flex-wrap">
              {["tr","en","es","de"].map((l) => (
                <button key={l} data-testid={`profile-lang-${l}`} onClick={() => setLang(l)}
                  className={`px-4 py-2 uppercase font-mono text-xs rounded-sm border ${lang === l ? "border-buy bg-buy-dim" : "border-line text-muted hover:bg-panel-hover"}`}>{l}</button>
              ))}
            </div>
          </div>
        </div>

        {/* Stats & Badges */}
        {stats && (
          <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-panel border border-line rounded-sm p-5 sm:p-6">
              <h2 className="text-sm font-bold tracking-widest uppercase mb-4 text-buy">{t("stats_title")}</h2>
              <div className="space-y-4">
                <Row k={t("stat_total_trades")} v={stats.total_trades} />
                <Row k={t("stat_won_trades")} v={stats.won_trades} />
                <Row k={t("stat_win_rate")} v={`%${stats.win_rate}`} />
              </div>
            </div>
            
            <div className="bg-panel border border-line rounded-sm p-5 sm:p-6">
              <h2 className="text-sm font-bold tracking-widest uppercase mb-4 text-yellow-500">{t("badges_title")}</h2>
              {stats.badges.length === 0 ? (
                <div className="text-dim text-xs py-4">{t("no_badges")}</div>
              ) : (
                <div className="grid grid-cols-2 gap-3">
                  {stats.badges.map(b => {
                    const Icon = badgeIcons[b.icon];
                    return (
                      <div key={b.id} className="bg-app border border-subtle rounded-sm p-3 flex flex-col items-center text-center gap-2">
                        {Icon && <Icon className="w-6 h-6 text-yellow-500" />}
                        <div className="text-[10px] font-bold uppercase tracking-widest">{b.name}</div>
                        <div className="text-[9px] text-dim">{b.description}</div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* 2FA */}
        <div className="mb-6">
          <TwoFactorPanel />
        </div>

        {/* KYC */}
        <KycPanel />

        {/* Sessions */}
        <div className="mb-6">
          <div className="bg-panel border border-line rounded-sm p-5 sm:p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-sm font-bold tracking-widest uppercase text-blue-400 flex items-center gap-2"><Monitor className="w-4 h-4" /> {t("sessions")}</h2>
              {sessions.length > 0 && (
                <button onClick={logoutAll} className="text-xs text-sell hover:text-red-400 flex items-center gap-1"><LogOut className="w-3 h-3" /> {t("logout_all")}</button>
              )}
            </div>
            {sessions.length === 0 ? (
              <p className="text-xs text-dim py-2">{t("no_sessions")}</p>
            ) : (
              <div className="space-y-2">
                {sessions.map((s) => (
                  <div key={s.id} className="flex items-center justify-between bg-app border border-subtle rounded-sm px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-mono truncate">{s.user_agent?.substring(0, 60) || t("unknown_device")}</div>
                      <div className="text-[10px] text-dim">{s.ip} • {s.created_at ? new Date(s.created_at).toLocaleString("tr-TR") : "—"}</div>
                    </div>
                    <button onClick={() => revokeSession(s.id)} className="text-sell hover:text-red-400 ml-2 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* API Keys */}
        <div className="mb-6">
          <div className="bg-panel border border-line rounded-sm p-5 sm:p-6">
            <h2 className="text-sm font-bold tracking-widest uppercase text-purple-400 flex items-center gap-2 mb-4"><Key className="w-4 h-4" /> {t("api_keys")}</h2>

            {newKeyResult && (
              <div className="mb-4 p-3 bg-buy/10 border border-buy/30 rounded-sm">
                <p className="text-xs text-buy font-bold mb-1">{t("new_api_key")}</p>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-xs bg-app px-2 py-1.5 rounded-sm font-mono break-all">{newKeyResult}</code>
                  <button onClick={() => copyToClipboard(newKeyResult)} className="shrink-0 text-buy hover:text-emerald-300"><Copy className="w-4 h-4" /></button>
                </div>
                <button onClick={() => setNewKeyResult(null)} className="text-xs text-dim mt-2 hover:text-white">{t("close")}</button>
              </div>
            )}

            <div className="flex gap-2 mb-4">
              <input value={newKeyName} onChange={(e) => setNewKeyName(e.target.value)} placeholder={t("api_key_name_placeholder")} className="flex-1 bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy"
                onKeyDown={(e) => e.key === "Enter" && createApiKey()} />
              <button onClick={createApiKey} disabled={!newKeyName.trim()} className="bg-buy text-black px-4 py-2 rounded-sm text-sm font-bold hover:bg-emerald-400 disabled:opacity-50 flex items-center gap-1">
                <Plus className="w-3.5 h-3.5" /> {t("create")}
              </button>
            </div>

            {apiKeys.length === 0 ? (
              <p className="text-xs text-dim py-2">{t("no_api_keys")}</p>
            ) : (
              <div className="space-y-2">
                {apiKeys.map((k) => (
                  <div key={k.id} className="flex items-center justify-between bg-app border border-subtle rounded-sm px-3 py-2.5">
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-bold">{k.name}</div>
                      <div className="text-[10px] text-dim font-mono">{k.key_prefix} • {k.created_at ? new Date(k.created_at).toLocaleDateString("tr-TR") : "—"}</div>
                    </div>
                    <button onClick={() => deleteApiKey(k.id)} className="text-sell hover:text-red-400 ml-2 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Bot Notifications */}
        <div className="mb-6">
          <div className="bg-panel border border-line rounded-sm p-5 sm:p-6">
            <h2 className="text-sm font-bold tracking-widest uppercase text-teal-400 flex items-center gap-2 mb-1"><MessageCircle className="w-4 h-4" /> {t("bot_notifications")}</h2>
            <p className="text-[10px] text-dim mb-4">{t("bot_desc")}</p>

            {/* Telegram */}
            <div className="mb-4 p-3 bg-app border border-subtle rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <MessageCircle className="w-4 h-4 text-blue-400" />
                <span className="text-xs font-bold uppercase tracking-widest">{t("telegram")}</span>
                {botSettings?.telegram_chat_id && (
                  <span className="ml-auto text-[10px] text-buy font-mono">✅ {t("connect")}ed</span>
                )}
              </div>
              {botSettings?.telegram_chat_id ? (
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[10px] bg-app-dark px-2 py-1.5 rounded-sm font-mono break-all">{botSettings.telegram_chat_id}</code>
                  <button onClick={disconnectTelegram} disabled={botBusy} className="text-sell hover:text-red-400 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> {t("disconnect")}</button>
                </div>
              ) : (
                <div>
                  {/* Step by step instructions */}
                  <div className="space-y-2 mb-3">
                    <div className="flex items-start gap-2.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] font-bold font-mono shrink-0 mt-0.5">1</span>
                      <div>
                        <span className="text-[11px] font-semibold text-white">{t("telegram_step1")}</span>
                        <p className="text-[9px] text-dim leading-relaxed">{t("telegram_step1_desc")}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] font-bold font-mono shrink-0 mt-0.5">2</span>
                      <div>
                        <span className="text-[11px] font-semibold text-white">{t("telegram_step2")}</span>
                        <p className="text-[9px] text-dim leading-relaxet">{t("telegram_step2_desc")}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-2.5">
                      <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-blue-500/20 text-blue-400 text-[10px] font-bold font-mono shrink-0 mt-0.5">3</span>
                      <div>
                        <span className="text-[11px] font-semibold text-white">{t("telegram_step3")}</span>
                        <p className="text-[9px] text-dim">{t("telegram_step3_desc")}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <input value={botForm.telegram_chat_id} onChange={(e) => setBotForm(f => ({ ...f, telegram_chat_id: e.target.value }))}
                        placeholder="123456789" className="w-full bg-app border border-line px-2 py-1.5 rounded-sm text-xs text-white outline-none focus:border-blue-400 font-mono" />
                    </div>
                    <button onClick={connectTelegram} disabled={!botForm.telegram_chat_id.trim() || botBusy}
                      className="bg-blue-500 text-black px-3 py-1.5 rounded-sm text-xs font-bold hover:bg-blue-400 disabled:opacity-50 self-start">{t("connect")}</button>
                  </div>
                </div>
              )}
            </div>

            {/* Discord */}
            <div className="mb-4 p-3 bg-app border border-subtle rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Webhook className="w-4 h-4 text-indigo-400" />
                <span className="text-xs font-bold uppercase tracking-widest">{t("discord")}</span>
                {botSettings?.discord_webhook_url && (
                  <span className="ml-auto text-[10px] text-buy font-mono">✅ {t("connect")}ed</span>
                )}
              </div>
              {botSettings?.discord_webhook_url ? (
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[10px] bg-app-dark px-2 py-1.5 rounded-sm font-mono break-all truncate">{botSettings.discord_webhook_url}</code>
                  <button onClick={disconnectDiscord} disabled={botBusy} className="text-sell hover:text-red-400 text-xs flex items-center gap-1"><Trash2 className="w-3 h-3" /> {t("disconnect")}</button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <input value={botForm.discord_webhook_url} onChange={(e) => setBotForm(f => ({ ...f, discord_webhook_url: e.target.value }))}
                      placeholder="https://discord.com/api/webhooks/..." className="w-full bg-app border border-line px-2 py-1.5 rounded-sm text-xs text-white outline-none focus:border-indigo-400 font-mono" />
                    <p className="text-[9px] text-dim mt-1">{t("discord_hint")}</p>
                  </div>
                  <button onClick={connectDiscord} disabled={!botForm.discord_webhook_url.trim() || botBusy}
                    className="bg-indigo-500 text-black px-3 py-1.5 rounded-sm text-xs font-bold hover:bg-indigo-400 disabled:opacity-50 self-start">{t("connect")}</button>
                </div>
              )}
            </div>

            {/* Event Toggles */}
            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest text-dim mb-2 flex items-center gap-2"><Bell className="w-3 h-3" /> {t("bot_events")}</div>
              <div className="flex flex-wrap gap-2">
                {[
                  ["trade_won", t("event_trade_won")],
                  ["trade_lost", t("event_trade_lost")],
                  ["trade_opened", t("event_trade_opened")],
                  ["limit_triggered", t("event_limit_triggered")],
                  ["alert_triggered", t("event_alert_triggered")],
                ].map(([evt, label]) => (
                  <button key={evt} onClick={() => toggleBotEvent(evt)}
                    className={`text-[10px] uppercase tracking-widest font-bold px-2.5 py-1.5 rounded-sm border transition-all ${
                      botEvents.includes(evt)
                        ? "border-teal-500 bg-teal-500/10 text-teal-400"
                        : "border-line text-dim hover:bg-panel-hover"
                    }`}>
                    {botEvents.includes(evt) ? "✓ " : ""}{label}
                  </button>
                ))}
              </div>
            </div>

            {/* Save */}
            <button onClick={saveBotSettings} disabled={botBusy}
              className="bg-teal-500 text-black px-4 py-2 rounded-sm text-xs font-bold hover:bg-teal-400 disabled:opacity-50 flex items-center gap-2">
              <Save className="w-3 h-3" /> {botBusy ? "..." : t("bot_save")}
            </button>
          </div>
        </div>

        {/* Risk Limits */}
        <div className="mb-6">
          <div className="bg-panel border border-line rounded-sm p-5 sm:p-6">
            <h2 className="text-sm font-bold tracking-widest uppercase text-orange-400 flex items-center gap-2 mb-4"><Shield className="w-4 h-4" /> {t("risk_limits")}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">{t("max_daily_loss")}</label>
                <input type="number" value={riskLimits.max_daily_loss} onChange={(e) => setRiskLimits(p => ({ ...p, max_daily_loss: e.target.value }))}
                  placeholder="Örn: 500" className="w-full bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">{t("max_concurrent_trades")}</label>
                <input type="number" value={riskLimits.max_concurrent_trades} onChange={(e) => setRiskLimits(p => ({ ...p, max_concurrent_trades: e.target.value }))}
                  placeholder="Örn: 5" className="w-full bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy font-mono" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-dim block mb-1">{t("max_position_size_label")}</label>
                <input type="number" value={riskLimits.max_position_size} onChange={(e) => setRiskLimits(p => ({ ...p, max_position_size: e.target.value }))}
                  placeholder="Örn: 1000" className="w-full bg-app border border-line px-3 py-2 rounded-sm text-sm outline-none focus:border-buy font-mono" />
              </div>
              <button onClick={saveRiskLimits} disabled={riskSaving}
                className="bg-orange-500 text-black px-4 py-2 rounded-sm text-sm font-bold hover:bg-orange-400 disabled:opacity-50 flex items-center gap-2">
                <Save className="w-3.5 h-3.5" /> {riskSaving ? t("saving") : t("save_limits")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const Row = ({ k, v }) => (
  <div className="flex justify-between items-center border-b border-subtle pb-2 last:border-0 last:pb-0">
    <span className="text-[10px] uppercase tracking-widest text-dim">{k}</span>
    <span className="font-mono text-sm">{v || "—"}</span>
  </div>
);
